import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageToggle } from '@/components/ui/LanguageSelector';
import { AlertCircle, ArrowRight, CheckCircle2, Eye, EyeOff, Loader2, Lock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Public password-reset completion page.
 *
 * Flow:
 *  1. Read `?token=…` from URL.
 *  2. Hit /auth/reset-password/verify so we can show an "expired/invalid"
 *     screen *before* the user types a new password (better UX than a
 *     post-submit 400).
 *  3. On submit: POST /auth/reset-password → backend updates pwd,
 *     consumes the token, kills all sessions for the user (forces fresh
 *     login). Redirect to /login.
 */
export function ResetPasswordPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFr = language === 'fr';

  const token = searchParams.get('token') || '';

  // null = checking, true = valid token, false = invalid/expired
  const [tokenValid, setTokenValid] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!token) {
      setTokenValid(false);
      return;
    }
    (async () => {
      try {
        const res = await authAPI.verifyResetToken(token);
        if (alive) setTokenValid(Boolean(res.data?.valid));
      } catch {
        if (alive) setTokenValid(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError(isFr ? 'Le mot de passe doit contenir au moins 8 caractères.' : 'Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(isFr ? 'Les deux mots de passe ne correspondent pas.' : 'Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.resetPassword({ token, new_password: newPassword });
      setSuccess(true);
      toast.success(isFr ? 'Mot de passe réinitialisé.' : 'Password reset.');
      // Brief delay so the user reads the success card before redirecting.
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      const code = err.response?.status;
      if (code === 400) {
        setError(isFr
          ? 'Le lien est invalide ou a expiré. Demandez-en un nouveau.'
          : 'The link is invalid or expired. Please request a new one.');
        setTokenValid(false);
      } else {
        setError(isFr ? 'Une erreur est survenue. Réessayez.' : 'Something went wrong. Try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --- States ---
  if (tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background p-4">
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" data-testid="reset-logo-link">
            <h1 className="font-heading text-4xl font-black text-foreground tracking-tight">
              Space<span className="text-primary">LeadX</span>
            </h1>
          </Link>
        </div>

        <Card className="shadow-xl">
          {tokenValid === false ? (
            <CardContent className="pt-8 pb-6 space-y-4" data-testid="reset-invalid-view">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <ShieldAlert className="h-6 w-6" />
              </div>
              <div className="text-center space-y-2">
                <h2 className="text-xl font-bold">
                  {isFr ? 'Lien invalide ou expiré' : 'Invalid or expired link'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {isFr
                    ? "Ce lien de réinitialisation n'est plus valide. Demandez-en un nouveau pour continuer."
                    : 'This reset link is no longer valid. Request a new one to continue.'}
                </p>
              </div>
              <Button asChild className="w-full" data-testid="reset-request-new-btn">
                <Link to="/forgot-password">
                  {isFr ? 'Demander un nouveau lien' : 'Request a new link'}
                </Link>
              </Button>
              <Button asChild variant="ghost" className="w-full" data-testid="reset-to-login-btn">
                <Link to="/login">{isFr ? 'Retour à la connexion' : 'Back to sign in'}</Link>
              </Button>
            </CardContent>
          ) : success ? (
            <CardContent className="pt-8 pb-6 space-y-4" data-testid="reset-success-view">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="text-center space-y-2">
                <h2 className="text-xl font-bold">
                  {isFr ? 'Mot de passe mis à jour' : 'Password updated'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {isFr
                    ? 'Pour votre sécurité, toutes vos sessions ont été déconnectées. Vous allez être redirigé vers la connexion.'
                    : 'For your security, all your sessions have been signed out. Redirecting to sign in…'}
                </p>
              </div>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">
                  {isFr ? 'Nouveau mot de passe' : 'New password'}
                </CardTitle>
                <CardDescription>
                  {isFr
                    ? 'Choisissez un nouveau mot de passe sécurisé. Toutes vos sessions actives seront déconnectées.'
                    : 'Choose a new secure password. All your active sessions will be signed out.'}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="reset-error-alert">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                  <div className="space-y-2">
                    <Label htmlFor="reset-new-password">{isFr ? 'Nouveau mot de passe' : 'New password'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="reset-new-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        minLength={8}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        className="pl-10 pr-10"
                        data-testid="reset-new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {isFr ? 'Au moins 8 caractères.' : 'At least 8 characters.'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reset-confirm-password">
                      {isFr ? 'Confirmer le mot de passe' : 'Confirm password'}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="reset-confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        minLength={8}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="pl-10"
                        data-testid="reset-confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 font-bold"
                    disabled={isLoading}
                    data-testid="reset-submit-btn"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {isFr ? 'Mettre à jour le mot de passe' : 'Update password'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
